import { useCallback } from 'react';
import useStore from './store/useStore';

const translations = {
  en: {
    // Nav
    'nav.howItWorks': 'How it Works',
    'nav.features': 'Features',
    'nav.pricing': 'Pricing',
    'nav.whoFor': 'Who it\'s for',
    'nav.contact': 'Contact',

    // Landing
    'landing.badge': 'Skip the Boilerplate. Ship Faster.',
    'landing.title1': 'Design Your Database.',
    'landing.title2': 'We Build & Deploy the API.',
    'landing.desc': 'Drag & drop your schema, generate a production-ready backend, then download the ZIP or deploy with one click — get a live API with interactive docs instantly.',
    'landing.andMore': 'more coming...',
    'landing.start': 'Start Free',
    'landing.demo': 'View Demo',
    'landing.login': 'Login',
    'landing.getStarted': 'Get Started',
    'landing.goToDashboard': 'Go to Dashboard',
    'landing.dashboard': 'Dashboard',
    'landing.feature1.title': 'Visual Schema Editor',
    'landing.feature1.desc': 'Design entities and relationships with an intuitive drag & drop canvas. Frontend devs can model a database without writing SQL.',
    'landing.feature2.title': 'Full Code Generation',
    'landing.feature2.desc': 'API endpoints, data models, business logic, auth, and interactive docs — all generated from your schema. Download the ZIP and self-host, anytime.',
    'landing.feature3.title': 'One-Click Deploy',
    'landing.feature3.desc': 'Skip the DevOps. Push your generated backend to a live URL with auto TLS, custom domain, and interactive docs — in 30 seconds.',
    'landing.footer': 'Backora — From schema to live API in minutes.',

    // Hero showcase (live product mockup)
    'showcase.tabDesign': 'Design',
    'showcase.tabLive': 'Live API',
    'showcase.caption': 'Draw your schema. We handle the backend, deployment, and everything in between.',

    // Hero feature card
    'hero.features.tag': 'What you get',
    'hero.feat1': 'One-click deploy from generated zip',
    'hero.feat2': 'Auto TLS / HTTPS (Let\'s Encrypt)',
    'hero.feat3': 'Wildcard subdomain + custom domain ready',
    'hero.feat4': 'Database provisioned and secured',
    'hero.feat5': 'Daily automated backups (Builder)',
    'hero.feat6': 'Built-in monitoring + auto-restart on crash',
    'hero.feat7': 'One-click upgrades and rollback',
    'hero.feat8': 'Email support, no Stack Overflow rabbit holes',

    // How it works
    'how.title': 'Three Steps to Your Live Backend',
    'how.desc': 'From idea to a working API in minutes, not days.',
    'how.step1.title': 'Design Your Schema',
    'how.step1.desc': 'Drag tables onto the canvas, add fields, define types and connect entities with relationships.',
    'how.step2.title': 'Configure Your Stack',
    'how.step2.desc': 'Toggle features like auth, validation, and API docs with a single click. Pick what your project needs.',
    'how.step3.title': 'Download or Deploy',
    'how.step3.desc': 'Get a production-ready ZIP and self-host, or deploy with one click — live API with interactive docs in 30 seconds.',

    // Features extended
    'features.title': 'Everything You Need',
    'features.autoSave.title': 'Auto-Save Drafts',
    'features.autoSave.desc': 'Every change is automatically saved. Come back anytime and continue where you left off.',
    'features.export.title': 'Clean JSON Export',
    'features.export.desc': 'Your entire schema is stored as clean JSON — easy to version control, share, or modify.',
    'features.darkMode.title': 'Dark & Light Mode',
    'features.darkMode.desc': 'Work comfortably day or night with beautiful dark and light themes.',

    // Who is it for
    'who.title': 'Built for Builders',
    'who.dev.title': 'Frontend & Mobile Devs',
    'who.dev.desc': 'Need a real backend, without writing one? Design it visually, generate the code, deploy in seconds — no backend skills required.',
    'who.student.title': 'Side Project Builders',
    'who.student.desc': 'Stop wrestling with infra and DevOps. Focus on what makes your project unique.',
    'who.startup.title': 'Founders & Indie Hackers',
    'who.startup.desc': 'Ship your MVP backend in minutes. No backend hire, no DevOps required.',
    'who.team.title': 'Small Teams & Agencies',
    'who.team.desc': 'Align on schema visually, ship together. One-click deploys for every client project.',

    // Pricing
    'pricing.title': 'Simple Pricing',
    'pricing.desc': 'Start free. Pay per project, only when you deploy.',
    'pricing.free': 'Free',
    'pricing.beta': 'CURRENT',
    'pricing.freePlan': 'Free',
    'pricing.freePrice': '$0',
    'pricing.freeDesc': 'Perfect for trying out a deployment',
    'pricing.free1': '1 active project per account',
    'pricing.free2': 'Runs 3 days, kept 7 more days',
    'pricing.free3': '256MB RAM, shared database',
    'pricing.free4': 'Subdomain on *.backora.dev',
    'pricing.free5': 'Sleeps after 30 min idle',
    'pricing.hobbyPlan': 'Hobby',
    'pricing.hobbyPrice': '$7.99',
    'pricing.hobbyDesc': 'For real production projects',
    'pricing.hobby1': 'Always-on, no sleep',
    'pricing.hobby2': '512MB RAM, dedicated database',
    'pricing.hobby3': '1GB database storage',
    'pricing.hobby4': 'Custom domain (CNAME)',
    'pricing.hobby5': '50GB bandwidth/month',
    'pricing.popular': 'Most Popular',
    'pricing.builderPlan': 'Builder',
    'pricing.builderPrice': '$17.99',
    'pricing.builderDesc': 'For real products with users',
    'pricing.builder1': 'Everything in Hobby',
    'pricing.builder2': '1GB RAM, 1 vCPU',
    'pricing.builder3': '5GB dedicated database',
    'pricing.builder4': 'Daily automated backups',
    'pricing.builder5': '200GB bandwidth, priority support',
    'pricing.month': '/mo',
    'pricing.perProject': '/project/mo',
    'pricing.current': 'Current Plan',
    'pricing.getStarted': 'Get Started',
    'pricing.comingSoon': 'Coming Soon',
    'pricing.betaNote': 'Per-project pricing. Pay only when you deploy. Cancel anytime.',

    // Comparison
    'comparison.title': 'Why pay for managed deployment?',
    'comparison.desc': 'Compare what you get with Backora vs DIY on a $5 VPS.',
    'comparison.backoraTitle': 'With Backora',
    'comparison.backoraSubtitle': 'Deploy in 30 seconds',
    'comparison.diyTitle': 'DIY on $5 VPS',
    'comparison.diySubtitle': '4–8 hours of setup + ongoing maintenance',
    'comparison.b1': 'One-click deploy from generated zip',
    'comparison.b2': 'Auto TLS / HTTPS (Let\'s Encrypt)',
    'comparison.b3': 'Wildcard subdomain + custom domain ready',
    'comparison.b4': 'Database provisioned and secured',
    'comparison.b5': 'Daily automated backups (Builder)',
    'comparison.b6': 'Built-in monitoring + auto-restart on crash',
    'comparison.b7': 'One-click upgrades and rollback',
    'comparison.b8': 'Email support, no Stack Overflow rabbit holes',
    'comparison.d1': 'Manual Docker, Nginx, env setup',
    'comparison.d2': 'Configure Let\'s Encrypt + cron renewal',
    'comparison.d3': 'DIY DNS records + per-domain certs',
    'comparison.d4': 'Install, secure, tune Postgres yourself',
    'comparison.d5': 'Write backup scripts + S3 wiring',
    'comparison.d6': 'Set up Prometheus / systemd / supervisord',
    'comparison.d7': 'Migrate manually, hope nothing breaks',
    'comparison.d8': 'Stack Overflow at 2 AM when prod is down',
    'comparison.cta': '$7.99 = save 4–8 hours of your time, every project.',

    // Contact
    'contact.title': 'Get in Touch',
    'contact.desc': 'Have questions, feedback, or want to collaborate? Reach out anytime.',

    // CTA
    'cta.title': 'Ready to Ship?',
    'cta.desc': 'Design your schema, generate the code, deploy in 30 seconds.',

    // Footer
    'footer.nav': 'Navigation',
    'footer.connect': 'Connect',
    'footer.rights': 'All rights reserved.',

    // Auth
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.name': 'Name',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.namePlaceholder': 'Your name',
    'auth.emailPlaceholder': 'email@example.com',
    'auth.passwordPlaceholder': 'password',
    'auth.wait': 'Please wait...',
    'auth.continueWithGoogle': 'Continue with Google',
    'auth.or': 'or',
    'auth.emailInvalid': 'Please enter a valid email address',
    'auth.emailDisposable': 'Disposable or temporary email addresses are not allowed',
    'auth.emailTld': 'This email domain is not allowed. Please use a real email address',
    'auth.emailTypo': 'Did you mean',

    // Home
    'home.hey': 'Hey',
    'home.subtitle': 'Your workspace is ready. Build something awesome today.',
    'home.newProject': 'New Project',
    'home.noProjects': 'No projects yet',
    'home.createFirst': 'Create your first project',
    'home.loading': 'Loading...',
    'home.generated': 'Generated',
    'home.expired': 'Expired',
    'home.draft': 'Draft',
    'home.download': 'Download',
    'home.edit': 'Edit',
    'home.continue': 'Continue',

    // Profile menu
    'profile.profile': 'Profile',
    'profile.darkMode': 'Dark Mode',
    'profile.lightMode': 'Light Mode',
    'profile.logout': 'Logout',
    'profile.logoutConfirm': 'Are you sure you want to logout?',
    'profile.cancel': 'Cancel',

    // Delete
    'delete.title': 'Delete Project',
    'delete.confirm': 'Are you sure you want to delete',
    'delete.undone': 'This action cannot be undone.',
    'delete.deleting': 'Deleting...',
    'delete.delete': 'Delete',

    // Profile page
    'profilePage.title': 'Profile Settings',
    'profilePage.back': 'Back to Dashboard',
    'profilePage.personal': 'Personal Information',
    'profilePage.changePassword': 'Change Password',
    'profilePage.passwordHint': 'Leave empty to keep current password',
    'profilePage.newPassword': 'New Password',
    'profilePage.newPasswordPlaceholder': 'New password (optional)',
    'profilePage.save': 'Save Changes',
    'profilePage.saving': 'Saving...',
    'profilePage.profileUpdated': 'Profile updated successfully',
    'profilePage.passwordUpdated': 'Profile and password updated',
    'profilePage.verified': 'Verified',
    'profilePage.notVerified': 'Not verified',
    'profilePage.verifyHint': 'Verify your email to recover your password if you forget it.',
    'profilePage.sendCode': 'Send Verification Code',
    'profilePage.sending': 'Sending...',
    'profilePage.codeSent': 'Verification code sent to your email',
    'profilePage.enterCode': 'Enter the code sent to',
    'profilePage.verify': 'Verify',
    'profilePage.resend': 'Resend code',
    'profilePage.resendIn': 'Resend in',
    'profilePage.emailVerified': 'Email verified successfully!',
    'profilePage.codeResent': 'Code resent to your email',

    // Editor - Toolbar
    'toolbar.brand': 'Backora',
    'toolbar.table': 'Table',
    'toolbar.enum': 'Enum',
    'toolbar.selectLang': 'Select a language first',
    'toolbar.project': 'Project',
    'toolbar.features': 'Features',
    'toolbar.database': 'Database',
    'toolbar.security': 'Security',
    'toolbar.projectName': 'Project Name',
    'toolbar.basePackage': 'Base Package',
    'toolbar.language': 'Language',
    'toolbar.selectLangOption': '-- Select --',
    'toolbar.buildTool': 'Build Tool',
    'toolbar.javaVersion': 'Java Version',
    'toolbar.springVersion': 'Spring Boot Version',
    'toolbar.packaging': 'Packaging',
    'toolbar.dbType': 'Database Type',
    'toolbar.username': 'Username',
    'toolbar.dbPassword': 'Password',
    'toolbar.dbPasswordAutoGen': 'Database password is auto-generated for security. See application-dev.yml in your zip.',
    'toolbar.jwtSecret': 'JWT Secret Key',
    'toolbar.jwtExpiration': 'Expiration Time (ms)',
    'toolbar.generate': 'Generate Project',
    'toolbar.generating': 'Generating...',
    'toolbar.noEntities': 'Hey, add a table first before generating!',

    // Editor - Sidebar
    'sidebar.fields': 'Fields',
    'sidebar.above': 'Above',
    'sidebar.below': 'Below',
    'sidebar.delete': 'Delete',
    'sidebar.deleteTable': 'Delete Table',

    // Connection popup
    'connection.title': 'Relationship Type',
    'connection.manyToOne': 'Many to One',
    'connection.oneToOne': 'One to One',
    'connection.cancel': 'Cancel',
    'canvas.replaceRelationTitle': 'Replace existing relation?',
    'canvas.replaceRelationConfirm': 'This field already has a relation. Replace it with the new connection?',
    'canvas.replaceRelationOk': 'Replace',

    // Generate result
    'gen.success': 'Project Generated!',
    'gen.failed': 'Generation Failed',
    'gen.downloadZip': 'Download ZIP',
    'gen.expires': 'Link expires',
    'gen.close': 'Close',
    'gen.tryAgain': 'Try Again',

    // Save
    'save.saving': 'Saving...',
    'save.saved': 'Saved',
    'save.error': 'Save failed',

    // Feedback
    'feedback.title': 'Feedback & Suggestions',
    'feedback.subtitle': 'Have feedback or suggestions? We\'d love to hear from you.',
    'feedback.backToDashboard': 'Back to Dashboard',
    'feedback.write': 'Write Feedback',
    'feedback.aboutLabel': 'About',
    'feedback.aboutPlatform': 'Platform',
    'feedback.aboutGenerated': 'Generated Project',
    'feedback.aboutOther': 'Other',
    'feedback.messageLabel': 'Your Feedback',
    'feedback.placeholder': 'Share your thoughts, suggestions or report an issue...',
    'feedback.submit': 'Submit Feedback',
    'feedback.submitting': 'Submitting...',
    'feedback.submitted': 'Thank you! Your feedback has been submitted.',
    'feedback.error': 'Failed to submit feedback. Please try again.',
    'feedback.noFeedbacks': 'No feedback yet',
    'feedback.writeFirst': 'Write your first feedback',
    'feedback.deleteTitle': 'Delete Feedback',
    'feedback.deleteConfirm': 'Are you sure you want to delete this feedback?',
    'feedback.prev': 'Previous',
    'feedback.next': 'Next',
    'feedback.menuItem': 'Feedback',
    'feedback.bannerTitle': 'Help us improve Backora!',
    'feedback.bannerDesc': 'This is a beta version. Your feedback and suggestions help us build a better product.',

    // Sidebar
    'sidebar.projects': 'Projects',
    'sidebar.myProjects': 'My Projects',
    'sidebar.chooseTemplate': 'Choose from templates',
    'sidebar.newProject': 'New Project',
    'sidebar.feedback': 'Feedback',
    'sidebar.profile': 'Profile',
    'sidebar.settings': 'Settings',
    'sidebar.admin': 'Admin Page',

    // Landing
    'landing.browseTemplates': 'Browse templates',

    // Templates
    'templates.heroTitle': 'Choose a starting point',
    'templates.heroSub': 'Pick a ready-made schema, customize it, and ship a backend in seconds.',
    'templates.use': 'Use this template',
    'templates.entities': 'entities',
    'templates.blog.name': 'Blog API',
    'templates.blog.desc': 'A classic blog backend: users, posts, comments and tags. Ready for content platforms or your portfolio site.',
    'templates.tasks.name': 'Task Tracker',
    'templates.tasks.desc': 'Project · Board · Task · Comment — a Trello-style task management API with users and assignments.',
    'templates.ecommerce.name': 'E-commerce Starter',
    'templates.ecommerce.desc': 'User · Category · Product · Order · OrderItem — basic shop catalog and checkout schema.',
    'templates.customBadge': 'Custom',

    // Admin templates
    'admin.templates': 'Templates',
    'admin.template.new': 'New template',
    'admin.template.edit': 'Edit',
    'admin.template.source': 'Source',
    'admin.template.entities': 'Entities',
    'admin.template.builtin': 'Built-in',
    'admin.template.custom': 'Custom',
    'admin.template.descShort': 'Description',
    'admin.template.empty': 'No templates yet',
    'admin.template.deleteTitle': 'Delete template',
    'admin.template.deleteConfirm': 'Are you sure you want to delete',
    'admin.template.namePlaceholder': 'Template name',
    'admin.template.descPlaceholder': 'Short description (shown to users)',
    'admin.template.color': 'Color',
    'admin.template.icon': 'Icon',
    'admin.template.save': 'Save template',
    'admin.template.saving': 'Saving...',
    'admin.template.nameRequired': 'Please enter a template name',
    'admin.template.backToAdmin': 'Back to admin',

    // Guest viewer
    'guest.previewMode': 'Preview mode — sign in to edit and generate',
    'guest.signInToEdit': 'Sign in to edit',
    'guest.backToTemplates': 'Back to templates',

    // Admin
    'admin.panel': 'Admin Panel',
    'admin.loginTitle': 'Admin Verification',
    'admin.loginIdleHint': 'Admin verification required. Click the button below to send a 6-digit code to the admin email.',
    'admin.loginHint': 'A 6-digit code has been sent to the admin email. Enter it below to continue.',
    'admin.sendCode': 'Send verification code',
    'admin.code': 'Verification code',
    'admin.codeSent': 'Code sent to admin email',
    'admin.codeLength': 'Please enter the 6-digit code',
    'admin.sendFailed': 'Failed to send code',
    'admin.verifyFailed': 'Invalid or expired code',
    'admin.verify': 'Verify & Continue',
    'admin.users': 'Users',
    'admin.projects': 'Projects',
    'admin.noUsers': 'No users found',
    'admin.noProjects': 'No projects found',
    'admin.locked': 'Admin panel is locked. Verify with a code sent to the admin email.',
    'admin.unlock': 'Unlock',
    'admin.signOut': 'Sign out of admin',
    'admin.col.id': 'ID',
    'admin.col.name': 'Name',
    'admin.col.email': 'Email',
    'admin.col.provider': 'Provider',
    'admin.col.verified': 'Verified',
    'admin.col.createdAt': 'Created',
    'admin.col.projectName': 'Project',
    'admin.col.owner': 'Owner',
    'admin.col.ownerEmail': 'Owner email',
    'admin.col.language': 'Language',
    'admin.col.status': 'Status',
    'admin.col.balance': 'Balance',
    'admin.col.expiredAt': 'Expires',
    'admin.status.generated': 'Generated',
    'admin.status.expired': 'Expired',
    'admin.status.draft': 'Draft',
    'admin.view': 'View',
    'admin.viewingBadge': 'VIEW ONLY',
    'admin.backToPanel': 'Back to admin panel',
    'admin.rowsPerPage': 'Rows per page',
    'admin.totalRows': 'total',

    // Relation hover preview mode
    'hoverMode.label': 'Hover:',
    'hoverMode.code': 'Code',
    'hoverMode.json': 'JSON',
    'hoverMode.tooltip': 'Choose preview style for relation hover',

    // Misc
    'error.connection': 'Could not connect to server',
  },

  uz: {
    'nav.howItWorks': 'Qanday ishlaydi',
    'nav.features': 'Imkoniyatlar',
    'nav.pricing': 'Narxlar',
    'nav.whoFor': 'Kimlar uchun',
    'nav.contact': 'Aloqa',

    'landing.badge': 'Skip the Boilerplate. Ship Faster.',
    'landing.title1': "Ma'lumotlar bazangizni loyihalang.",
    'landing.title2': "API qurish va deploy biz tomonimizdan.",
    'landing.desc': "Drag & drop bilan sxemani yarating, tayyor backend generate qiling, keyin ZIP yuklab oling yoki bir tugma bilan deploy qiling — interaktiv docs bilan tirik API darhol oling.",
    'landing.andMore': "va yana ko'plab...",
    'landing.start': 'Bepul boshlang',
    'landing.demo': 'Demo',
    'landing.login': 'Kirish',
    'landing.getStarted': 'Boshlash',
    'landing.goToDashboard': 'Boshqaruv paneliga',
    'landing.dashboard': 'Boshqaruv paneli',
    'landing.feature1.title': 'Vizual sxema muharriri',
    'landing.feature1.desc': "Drag & drop canvas bilan entity va bog'lanishlarni loyihalang. Frontend dev ham SQL yozmasdan baza model qila oladi.",
    'landing.feature2.title': "To'liq kod generatsiya",
    'landing.feature2.desc': "API endpoint'lar, ma'lumot modellari, biznes logika, auth va interaktiv docs — sxemangizdan generate qilinadi. ZIP'ni yuklab olib istalgan paytda o'zingiz host qiling.",
    'landing.feature3.title': "Bir tugma bilan deploy",
    'landing.feature3.desc': "DevOps'dan voz keching. Generated backend'ni live URL'ga, avto TLS, custom domain va interaktiv docs bilan — 30 sekundda chiqaring.",
    'landing.footer': "Backora — Sxemadan tirik API'gacha bir necha daqiqada.",

    // Hero showcase (live product mockup)
    'showcase.tabDesign': 'Loyihalash',
    'showcase.tabLive': 'Tirik API',
    'showcase.caption': "Sxemani chizing. Backend, deploy va hammasini biz qilamiz.",

    // Hero feature card
    'hero.features.tag': "Nima olasiz",
    'hero.feat1': "Generated zip'dan bir tugma bilan deploy",
    'hero.feat2': "Avtomatik TLS / HTTPS (Let's Encrypt)",
    'hero.feat3': "Wildcard subdomain + custom domain tayyor",
    'hero.feat4': "Database tayyor va himoyalangan",
    'hero.feat5': "Kunlik avtomatik backup (Builder)",
    'hero.feat6': "Built-in monitoring + crash'da avto-restart",
    'hero.feat7': "Bir tugma upgrade va rollback",
    'hero.feat8': "Email support, Stack Overflow'da qidirish yo'q",

    'how.title': "Uch qadamda tirik backend",
    'how.desc': "G'oyadan ishlaydigan API'gacha bir necha daqiqada, kunlarda emas.",
    'how.step1.title': "Sxemani loyihalang",
    'how.step1.desc': "Jadvallarni canvasga tashlang, maydonlar qo'shing, turlarni belgilang va entity'larni bog'lang.",
    'how.step2.title': "Stackni sozlang",
    'how.step2.desc': "Auth, validatsiya, API docs kabi feature'larni bitta bosish bilan yoqing. Loyihangizga kerak narsani tanlang.",
    'how.step3.title': "Yuklang yoki deploy qiling",
    'how.step3.desc': "Tayyor ZIP olib o'zingiz host qiling, yoki bir tugma bilan deploy qiling — 30 sekundda interaktiv docs bilan tirik API.",

    'features.title': "Sizga kerak bo'lgan hamma narsa",
    'features.autoSave.title': "Avto-saqlash",
    'features.autoSave.desc': "Har bir o'zgarish avtomatik saqlanadi. Istalgan paytda qaytib davom ettiring.",
    'features.export.title': "Toza JSON eksport",
    'features.export.desc': "Butun sxema toza JSON sifatida saqlanadi — versiya boshqaruvi, ulashish yoki o'zgartirish oson.",
    'features.darkMode.title': "Qorong'u va yorug' rejim",
    'features.darkMode.desc': "Kunduz yoki tunda qulay ishlang — chiroyli qorong'u va yorug' mavzular bilan.",

    'who.title': "Yaratuvchilar uchun qurilgan",
    'who.dev.title': "Frontend va Mobile dev'lar",
    'who.dev.desc': "Tirik backend kerakmi, lekin uni yozishni bilmaysizmi? Vizual loyihalang, kod generate qiling, sekundlarda deploy qiling — backend ko'nikmasi shart emas.",
    'who.student.title': "Side project quruvchilar",
    'who.student.desc': "Infra va DevOps bilan kurashishni to'xtating. Loyihangizning unikal qismiga e'tibor bering.",
    'who.startup.title': "Founder va indie hacker'lar",
    'who.startup.desc': "MVP backend'ingizni daqiqalarda chiqaring. Backend dev yollash kerak emas, DevOps shart emas.",
    'who.team.title': "Kichik jamoalar va agentliklar",
    'who.team.desc': "Sxemani vizual muvofiqlashtiring, birga ship qiling. Har klient loyihasi uchun bir tugma deploy.",

    // Pricing
    'pricing.title': 'Oddiy narxlar',
    'pricing.desc': 'Bepul boshlang. Faqat deploy qilganingizda har loyiha uchun to\'lang.',
    'pricing.free': 'Bepul',
    'pricing.beta': 'JORIY',
    'pricing.freePlan': 'Bepul',
    'pricing.freePrice': '$0',
    'pricing.freeDesc': 'Deploy\'ni sinab ko\'rish uchun',
    'pricing.free1': 'Akkauntiga 1 ta aktiv loyiha',
    'pricing.free2': '3 kun ishlaydi, +7 kun saqlanadi',
    'pricing.free3': '256MB RAM, umumiy ma\'lumotlar bazasi',
    'pricing.free4': 'Subdomain *.backora.dev',
    'pricing.free5': '30 daqiqa idle\'da uxlaydi',
    'pricing.hobbyPlan': 'Hobby',
    'pricing.hobbyPrice': '$7.99',
    'pricing.hobbyDesc': 'Real production loyihalar uchun',
    'pricing.hobby1': 'Doim yoqiq, uxlamaydi',
    'pricing.hobby2': '512MB RAM, alohida ma\'lumotlar bazasi',
    'pricing.hobby3': '1GB DB storage',
    'pricing.hobby4': 'Custom domain (CNAME)',
    'pricing.hobby5': '50GB bandwidth/oy',
    'pricing.popular': 'Eng mashhur',
    'pricing.builderPlan': 'Builder',
    'pricing.builderPrice': '$17.99',
    'pricing.builderDesc': 'Foydalanuvchili real mahsulot uchun',
    'pricing.builder1': 'Hobby\'dagi hammasi',
    'pricing.builder2': '1GB RAM, 1 vCPU',
    'pricing.builder3': '5GB alohida database',
    'pricing.builder4': 'Kunlik avto-backup',
    'pricing.builder5': '200GB bandwidth, priority qo\'llab-quvvatlash',
    'pricing.month': '/oy',
    'pricing.perProject': '/loyiha/oy',
    'pricing.current': 'Joriy tarif',
    'pricing.getStarted': 'Boshlash',
    'pricing.comingSoon': 'Tez kunda',
    'pricing.betaNote': 'Har loyiha uchun alohida narx. Faqat deploy qilganingizda to\'laysiz. Istalgan vaqtda bekor qilasiz.',

    // Comparison
    'comparison.title': 'Nega managed deploy uchun to\'lash kerak?',
    'comparison.desc': 'Backora va $5 VPS\'da o\'zingiz qilishni solishtiring.',
    'comparison.backoraTitle': 'Backora bilan',
    'comparison.backoraSubtitle': '30 sekundda deploy',
    'comparison.diyTitle': '$5 VPS\'da o\'zingiz',
    'comparison.diySubtitle': '4–8 soat sozlash + doimiy parvarish',
    'comparison.b1': 'Generated zip\'dan bir tugma deploy',
    'comparison.b2': 'Avtomatik TLS / HTTPS (Let\'s Encrypt)',
    'comparison.b3': 'Wildcard subdomain + custom domain tayyor',
    'comparison.b4': 'Database tayyor va himoyalangan',
    'comparison.b5': 'Kunlik avtomatik backup (Builder)',
    'comparison.b6': 'Built-in monitoring + crash\'da avto-restart',
    'comparison.b7': 'Bir tugma upgrade va rollback',
    'comparison.b8': 'Email support, Stack Overflow\'da soatlab izlanish yo\'q',
    'comparison.d1': 'Qo\'lda Docker, Nginx, env sozlash',
    'comparison.d2': 'Let\'s Encrypt + cron renewal sozlash',
    'comparison.d3': 'O\'zingiz DNS yozuvlar + per-domen cert',
    'comparison.d4': 'Postgres\'ni o\'zingiz install, secure, tune qilish',
    'comparison.d5': 'Backup script\'lar yozish + S3 ulanish',
    'comparison.d6': 'Prometheus / systemd / supervisord sozlash',
    'comparison.d7': 'Qo\'lda migratsiya, hech narsa buzilmasligini umid qilish',
    'comparison.d8': 'Tunda 2 da prod down bo\'lsa, Stack Overflow',
    'comparison.cta': '$7.99 = har loyihada 4–8 soatlik vaqtingizni tejaysiz.',

    'contact.title': "Bog'lanish",
    'contact.desc': "Savollar, fikr-mulohazalar yoki hamkorlik? Istalgan vaqtda murojaat qiling.",

    'cta.title': "Ship qilishga tayyormisiz?",
    'cta.desc': "Sxemangizni loyihalang, kod generate qiling, 30 sekundda deploy qiling.",

    'footer.nav': "Navigatsiya",
    'footer.connect': "Aloqa",
    'footer.rights': "Barcha huquqlar himoyalangan.",

    'auth.login': 'Kirish',
    'auth.register': "Ro'yxatdan o'tish",
    'auth.name': 'Ism',
    'auth.email': 'Email',
    'auth.password': 'Parol',
    'auth.namePlaceholder': 'Ismingiz',
    'auth.emailPlaceholder': 'email@example.com',
    'auth.passwordPlaceholder': 'parol',
    'auth.wait': 'Iltimos kuting...',
    'auth.continueWithGoogle': 'Google orqali davom etish',
    'auth.or': 'yoki',
    'auth.emailInvalid': 'Yaroqli email manzilini kiriting',
    'auth.emailDisposable': 'Vaqtinchalik yoki bir martalik emaillarga ruxsat berilmaydi',
    'auth.emailTld': 'Bu email domeni qabul qilinmaydi. Iltimos, haqiqiy email manzilidan foydalaning',
    'auth.emailTypo': 'Balki quyidagini nazarda tutdingizmi:',

    'toolbar.noEntities': 'Hey, avval jadval qo\'shing keyin generate qiling!',

    'home.hey': 'Salom',
    'home.subtitle': "Ish maydoningiz tayyor. Bugun ajoyib narsa yarating.",
    'home.newProject': 'Yangi loyiha',
    'home.noProjects': 'Hali loyihalar yo\'q',
    'home.createFirst': 'Birinchi loyihangizni yarating',
    'home.loading': 'Yuklanmoqda...',
    'home.generated': 'Yaratilgan',
    'home.expired': 'Muddati tugagan',
    'home.draft': 'Qoralama',
    'home.download': 'Yuklab olish',
    'home.edit': 'Tahrirlash',
    'home.continue': 'Davom etish',

    'profile.profile': 'Profil',
    'profile.darkMode': 'Qorong\'u rejim',
    'profile.lightMode': 'Yorug\' rejim',
    'profile.logout': 'Chiqish',
    'profile.logoutConfirm': 'Rostdan ham chiqmoqchimisiz?',
    'profile.cancel': 'Bekor qilish',

    'delete.title': 'Loyihani o\'chirish',
    'delete.confirm': "Rostdan ham o'chirmoqchimisiz",
    'delete.undone': 'Bu amalni qaytarib bo\'lmaydi.',
    'delete.deleting': 'O\'chirilmoqda...',
    'delete.delete': 'O\'chirish',

    'profilePage.title': 'Profil sozlamalari',
    'profilePage.back': 'Boshqaruv paneliga qaytish',
    'profilePage.personal': 'Shaxsiy ma\'lumotlar',
    'profilePage.changePassword': 'Parolni o\'zgartirish',
    'profilePage.passwordHint': 'Joriy parolni saqlash uchun bo\'sh qoldiring',
    'profilePage.newPassword': 'Yangi parol',
    'profilePage.newPasswordPlaceholder': 'Yangi parol (ixtiyoriy)',
    'profilePage.save': 'Saqlash',
    'profilePage.saving': 'Saqlanmoqda...',
    'profilePage.profileUpdated': 'Profil muvaffaqiyatli yangilandi',
    'profilePage.passwordUpdated': 'Profil va parol yangilandi',
    'profilePage.verified': 'Tasdiqlangan',
    'profilePage.notVerified': 'Tasdiqlanmagan',
    'profilePage.verifyHint': 'Parolni unutib qo\'ysangiz tiklash uchun emailni tasdiqlang.',
    'profilePage.sendCode': 'Tasdiqlash kodini yuborish',
    'profilePage.sending': 'Yuborilmoqda...',
    'profilePage.codeSent': 'Tasdiqlash kodi emailingizga yuborildi',
    'profilePage.enterCode': 'Yuborilgan kodni kiriting',
    'profilePage.verify': 'Tasdiqlash',
    'profilePage.resend': 'Qayta yuborish',
    'profilePage.resendIn': 'Qayta yuborish',
    'profilePage.emailVerified': 'Email muvaffaqiyatli tasdiqlandi!',
    'profilePage.codeResent': 'Kod qayta yuborildi',

    // Feedback
    'feedback.title': 'Fikr-mulohazalar va takliflar',
    'feedback.subtitle': 'Fikr yoki takliflaringiz bormi? Biz sizni eshitishdan xursandmiz.',
    'feedback.backToDashboard': 'Boshqaruv paneliga qaytish',
    'feedback.write': 'Fikr yozish',
    'feedback.aboutLabel': 'Mavzu',
    'feedback.aboutPlatform': 'Platforma',
    'feedback.aboutGenerated': 'Yaratilgan loyiha',
    'feedback.aboutOther': 'Boshqa',
    'feedback.messageLabel': 'Fikringiz',
    'feedback.placeholder': 'Fikrlaringiz, takliflaringiz yoki muammolarni yozing...',
    'feedback.submit': 'Yuborish',
    'feedback.submitting': 'Yuborilmoqda...',
    'feedback.submitted': 'Rahmat! Fikringiz yuborildi.',
    'feedback.error': 'Fikr yuborishda xatolik. Qaytadan urinib ko\'ring.',
    'feedback.noFeedbacks': 'Hali fikrlar yo\'q',
    'feedback.writeFirst': 'Birinchi fikringizni yozing',
    'feedback.deleteTitle': 'Fikrni o\'chirish',
    'feedback.deleteConfirm': 'Rostdan ham bu fikrni o\'chirmoqchimisiz?',
    'feedback.prev': 'Oldingi',
    'feedback.next': 'Keyingi',
    'feedback.menuItem': 'Fikr-mulohaza',
    'feedback.bannerTitle': 'Backora ni yaxshilashga yordam bering!',
    'feedback.bannerDesc': 'Bu beta versiya. Fikr va takliflaringiz yaxshiroq mahsulot yaratishga yordam beradi.',

    // Sidebar
    'sidebar.projects': 'Loyihalar',
    'sidebar.myProjects': 'Mening loyihalarim',
    'sidebar.chooseTemplate': 'Shablonlardan tanlang',
    'sidebar.newProject': 'Yangi loyiha',
    'sidebar.feedback': 'Fikr-mulohaza',
    'sidebar.profile': 'Profil',
    'sidebar.settings': 'Sozlamalar',
    'sidebar.admin': 'Admin sahifa',

    'landing.browseTemplates': 'Shablonlarni ko\'rish',

    'templates.heroTitle': 'Boshlash uchun shablon tanlang',
    'templates.heroSub': 'Tayyor sxemani tanlang, sozlang va backendni soniyalarda yarating.',
    'templates.use': 'Shu shablonni ishlatish',
    'templates.entities': 'jadval',
    'templates.blog.name': 'Blog API',
    'templates.blog.desc': 'Klassik blog backend: foydalanuvchilar, postlar, izohlar va teglar. Kontent platformalari yoki portfolio sayti uchun tayyor.',
    'templates.tasks.name': 'Task Tracker',
    'templates.tasks.desc': 'Project · Board · Task · Comment — foydalanuvchilar va vazifalar bilan Trello uslubidagi vazifa boshqaruv API.',
    'templates.ecommerce.name': 'E-commerce Starter',
    'templates.ecommerce.desc': 'User · Category · Product · Order · OrderItem — oddiy do\'kon katalog va checkout sxemasi.',
    'templates.customBadge': 'Maxsus',

    'admin.templates': 'Shablonlar',
    'admin.template.new': 'Yangi shablon',
    'admin.template.edit': 'Tahrirlash',
    'admin.template.source': 'Manba',
    'admin.template.entities': 'Jadvallar',
    'admin.template.builtin': 'Tizimda',
    'admin.template.custom': 'Maxsus',
    'admin.template.descShort': 'Tavsif',
    'admin.template.empty': 'Hali shablonlar yo\'q',
    'admin.template.deleteTitle': 'Shablonni o\'chirish',
    'admin.template.deleteConfirm': 'Rostan ham o\'chirmoqchimisiz',
    'admin.template.namePlaceholder': 'Shablon nomi',
    'admin.template.descPlaceholder': 'Qisqa tavsif (foydalanuvchilarga ko\'rinadi)',
    'admin.template.color': 'Rang',
    'admin.template.icon': 'Ikona',
    'admin.template.save': 'Shablonni saqlash',
    'admin.template.saving': 'Saqlanmoqda...',
    'admin.template.nameRequired': 'Iltimos, shablon nomini kiriting',
    'admin.template.backToAdmin': 'Admin paneliga qaytish',

    'guest.previewMode': 'Ko\'rish rejimi — tahrirlash va generate qilish uchun login qiling',
    'guest.signInToEdit': 'Tahrirlash uchun kiring',
    'guest.backToTemplates': 'Shablonlarga qaytish',

    // Admin
    'admin.panel': 'Admin panel',
    'admin.loginTitle': 'Admin tasdiqlash',
    'admin.loginIdleHint': 'Admin tasdiqlash talab qilinadi. Admin emailga 6 raqamli kod yuborish uchun tugmani bosing.',
    'admin.loginHint': 'Admin emailga 6 raqamli kod yuborildi. Davom etish uchun uni kiriting.',
    'admin.sendCode': 'Tasdiqlash kodini yuborish',
    'admin.code': 'Tasdiqlash kodi',
    'admin.codeSent': 'Kod admin emailga yuborildi',
    'admin.codeLength': '6 raqamli kodni kiriting',
    'admin.sendFailed': 'Kod yuborishda xatolik',
    'admin.verifyFailed': 'Kod noto\'g\'ri yoki muddati tugagan',
    'admin.verify': 'Tasdiqlash',
    'admin.users': 'Foydalanuvchilar',
    'admin.projects': 'Loyihalar',
    'admin.noUsers': 'Foydalanuvchilar topilmadi',
    'admin.noProjects': 'Loyihalar topilmadi',
    'admin.locked': 'Admin panel yopiq. Admin emailga yuborilgan kod bilan tasdiqlang.',
    'admin.unlock': 'Ochish',
    'admin.signOut': 'Admin seansdan chiqish',
    'admin.col.id': 'ID',
    'admin.col.name': 'Ism',
    'admin.col.email': 'Email',
    'admin.col.provider': 'Provider',
    'admin.col.verified': 'Tasdiqlangan',
    'admin.col.createdAt': 'Yaratilgan',
    'admin.col.projectName': 'Loyiha',
    'admin.col.owner': 'Egasi',
    'admin.col.ownerEmail': 'Egasining emaili',
    'admin.col.language': 'Til',
    'admin.col.status': 'Status',
    'admin.col.balance': 'Balans',
    'admin.col.expiredAt': 'Muddat',
    'admin.status.generated': 'Yaratilgan',
    'admin.status.expired': 'Muddati tugagan',
    'admin.status.draft': 'Qoralama',
    'admin.view': 'Ko\'rish',
    'admin.viewingBadge': 'FAQAT KO\'RISH',
    'admin.backToPanel': 'Admin panelga qaytish',
    'admin.rowsPerPage': 'Bir sahifada',
    'admin.totalRows': 'jami',

    // Relation hover preview mode
    'hoverMode.label': 'Hover:',
    'hoverMode.code': 'Kod',
    'hoverMode.json': 'JSON',
    'hoverMode.tooltip': 'Aloqa hover\'i uchun ko\'rinish turini tanlang',

    'error.connection': 'Serverga ulanib bo\'lmadi',
  },

  ru: {
    'nav.howItWorks': 'Как это работает',
    'nav.features': 'Возможности',
    'nav.pricing': 'Цены',
    'nav.whoFor': 'Для кого',
    'nav.contact': 'Контакты',

    'landing.badge': 'Skip the Boilerplate. Ship Faster.',
    'landing.title1': 'Спроектируйте базу данных.',
    'landing.title2': 'API и деплой — на нас.',
    'landing.desc': 'Drag & drop вашу схему, генерируйте готовый бэкенд, затем скачайте ZIP или задеплойте в один клик — получите живой API с интерактивными доками мгновенно.',
    'landing.andMore': 'и другие...',
    'landing.start': 'Начать бесплатно',
    'landing.demo': 'Демо',
    'landing.login': 'Войти',
    'landing.getStarted': 'Начать',
    'landing.goToDashboard': 'Перейти в панель',
    'landing.dashboard': 'Панель управления',
    'landing.feature1.title': 'Визуальный редактор схемы',
    'landing.feature1.desc': 'Проектируйте сущности и связи с интуитивным drag & drop холстом. Frontend разработчик может моделировать БД без SQL.',
    'landing.feature2.title': 'Полная генерация кода',
    'landing.feature2.desc': 'API эндпоинты, модели данных, бизнес-логика, авторизация и интерактивные доки — генерируются из схемы. Скачайте ZIP и хостите сами в любой момент.',
    'landing.feature3.title': 'Деплой в один клик',
    'landing.feature3.desc': 'Без DevOps. Задеплойте сгенерированный бэкенд на живой URL с авто TLS, своим доменом и интерактивными доками — за 30 секунд.',
    'landing.footer': 'Backora — От схемы до живого API за минуты.',

    // Hero showcase (live product mockup)
    'showcase.tabDesign': 'Дизайн',
    'showcase.tabLive': 'Живой API',
    'showcase.caption': 'Нарисуйте схему. Бэкенд, деплой и всё остальное — на нас.',

    // Hero feature card
    'hero.features.tag': 'Что вы получаете',
    'hero.feat1': 'Деплой из zip в один клик',
    'hero.feat2': 'Автоматический TLS / HTTPS (Let\'s Encrypt)',
    'hero.feat3': 'Wildcard сабдомен + свой домен готовы',
    'hero.feat4': 'База данных поднята и защищена',
    'hero.feat5': 'Ежедневные авто-бэкапы (Builder)',
    'hero.feat6': 'Встроенный мониторинг + авто-рестарт',
    'hero.feat7': 'Апгрейд и откат в один клик',
    'hero.feat8': 'Email поддержка, без часов на Stack Overflow',

    'how.title': 'Три шага до живого бэкенда',
    'how.desc': 'От идеи до работающего API за минуты, а не дни.',
    'how.step1.title': 'Спроектируйте схему',
    'how.step1.desc': 'Перетаскивайте таблицы на холст, добавляйте поля, определяйте типы и связи.',
    'how.step2.title': 'Настройте стек',
    'how.step2.desc': 'Включите фичи как авторизация, валидация и API доки одним кликом. Выберите то что нужно проекту.',
    'how.step3.title': 'Скачайте или деплойте',
    'how.step3.desc': 'Получите готовый ZIP и хостите сами, или задеплойте в один клик — живой API с интерактивными доками за 30 секунд.',

    'features.title': 'Всё что нужно',
    'features.autoSave.title': 'Автосохранение черновиков',
    'features.autoSave.desc': 'Каждое изменение сохраняется автоматически. Вернитесь в любой момент.',
    'features.export.title': 'Чистый JSON экспорт',
    'features.export.desc': 'Вся схема хранится как чистый JSON — легко версионировать, делиться и менять.',
    'features.darkMode.title': 'Тёмная и светлая тема',
    'features.darkMode.desc': 'Работайте комфортно днём и ночью с красивыми темами.',

    'who.title': 'Создано для создателей',
    'who.dev.title': 'Frontend и Mobile разработчики',
    'who.dev.desc': 'Нужен живой бэкенд, но не умеете его писать? Спроектируйте визуально, сгенерируйте код, задеплойте за секунды — навыки бэкенда не нужны.',
    'who.student.title': 'Создатели pet-проектов',
    'who.student.desc': 'Хватит бороться с инфрой и DevOps. Сфокусируйтесь на том, что делает ваш проект уникальным.',
    'who.startup.title': 'Founder и indie-хакеры',
    'who.startup.desc': 'Запустите MVP бэкенд за минуты. Без найма бэкендера, без DevOps.',
    'who.team.title': 'Малые команды и агентства',
    'who.team.desc': 'Согласуйте схему визуально, шипьте вместе. Деплой в один клик для каждого клиентского проекта.',

    // Pricing
    'pricing.title': 'Простые цены',
    'pricing.desc': 'Начните бесплатно. Платите за каждый проект только при деплое.',
    'pricing.free': 'Бесплатно',
    'pricing.beta': 'ТЕКУЩИЙ',
    'pricing.freePlan': 'Бесплатно',
    'pricing.freePrice': '$0',
    'pricing.freeDesc': 'Чтобы попробовать деплой',
    'pricing.free1': '1 активный проект на аккаунт',
    'pricing.free2': 'Работает 3 дня, хранится +7 дней',
    'pricing.free3': '256MB RAM, общая база данных',
    'pricing.free4': 'Сабдомен на *.backora.dev',
    'pricing.free5': 'Засыпает после 30 мин простоя',
    'pricing.hobbyPlan': 'Hobby',
    'pricing.hobbyPrice': '$7.99',
    'pricing.hobbyDesc': 'Для реальных продакшен проектов',
    'pricing.hobby1': 'Всегда онлайн, не засыпает',
    'pricing.hobby2': '512MB RAM, выделенная база данных',
    'pricing.hobby3': '1GB места под базу данных',
    'pricing.hobby4': 'Свой домен (CNAME)',
    'pricing.hobby5': '50GB трафика/мес',
    'pricing.popular': 'Популярный',
    'pricing.builderPlan': 'Builder',
    'pricing.builderPrice': '$17.99',
    'pricing.builderDesc': 'Для реальных продуктов с пользователями',
    'pricing.builder1': 'Всё из Hobby',
    'pricing.builder2': '1GB RAM, 1 vCPU',
    'pricing.builder3': '5GB выделенная база данных',
    'pricing.builder4': 'Ежедневные авто-бэкапы',
    'pricing.builder5': '200GB трафика, приоритетная поддержка',
    'pricing.month': '/мес',
    'pricing.perProject': '/проект/мес',
    'pricing.current': 'Текущий план',
    'pricing.getStarted': 'Начать',
    'pricing.comingSoon': 'Скоро',
    'pricing.betaNote': 'Цена за каждый проект. Платите только при деплое. Отмените в любое время.',

    // Comparison
    'comparison.title': 'Зачем платить за управляемый деплой?',
    'comparison.desc': 'Сравните Backora с самостоятельным хостингом на $5 VPS.',
    'comparison.backoraTitle': 'С Backora',
    'comparison.backoraSubtitle': 'Деплой за 30 секунд',
    'comparison.diyTitle': 'Сам на $5 VPS',
    'comparison.diySubtitle': '4–8 часов настройки + постоянное обслуживание',
    'comparison.b1': 'Деплой из zip в один клик',
    'comparison.b2': 'Автоматический TLS / HTTPS (Let\'s Encrypt)',
    'comparison.b3': 'Wildcard сабдомен + свой домен готовы',
    'comparison.b4': 'База данных поднята и защищена',
    'comparison.b5': 'Ежедневные авто-бэкапы (Builder)',
    'comparison.b6': 'Встроенный мониторинг + авто-рестарт при крашe',
    'comparison.b7': 'Апгрейд и откат в один клик',
    'comparison.b8': 'Email поддержка, без часов на Stack Overflow',
    'comparison.d1': 'Вручную Docker, Nginx, env',
    'comparison.d2': 'Настроить Let\'s Encrypt + cron renewal',
    'comparison.d3': 'Свои DNS записи + сертификат на каждый домен',
    'comparison.d4': 'Установить, защитить, настроить Postgres',
    'comparison.d5': 'Писать скрипты бэкапа + интеграция с S3',
    'comparison.d6': 'Настроить Prometheus / systemd / supervisord',
    'comparison.d7': 'Мигрировать вручную, надеясь что не сломается',
    'comparison.d8': 'Stack Overflow в 2 ночи когда прод лежит',
    'comparison.cta': '$7.99 = экономия 4–8 часов вашего времени на каждом проекте.',

    'contact.title': 'Свяжитесь с нами',
    'contact.desc': 'Есть вопросы, отзывы или предложения? Пишите в любое время.',

    'cta.title': 'Готовы шипить?',
    'cta.desc': 'Спроектируйте схему, сгенерируйте код, задеплойте за 30 секунд.',

    'footer.nav': 'Навигация',
    'footer.connect': 'Связь',
    'footer.rights': 'Все права защищены.',

    'auth.login': 'Войти',
    'auth.register': 'Регистрация',
    'auth.name': 'Имя',
    'auth.email': 'Email',
    'auth.password': 'Пароль',
    'auth.namePlaceholder': 'Ваше имя',
    'auth.emailPlaceholder': 'email@example.com',
    'auth.passwordPlaceholder': 'пароль',
    'auth.wait': 'Подождите...',
    'auth.continueWithGoogle': 'Продолжить с Google',
    'auth.or': 'или',
    'auth.emailInvalid': 'Введите корректный email адрес',
    'auth.emailDisposable': 'Временные или одноразовые email адреса не разрешены',
    'auth.emailTld': 'Этот email домен не разрешён. Используйте настоящий email адрес',
    'auth.emailTypo': 'Возможно вы имели в виду:',

    'home.hey': 'Привет',
    'home.subtitle': 'Ваше рабочее пространство готово. Создайте что-то крутое сегодня.',
    'toolbar.noEntities': 'Эй, сначала добавь таблицу, потом генерируй!',

    'home.newProject': 'Новый проект',
    'home.noProjects': 'Проектов пока нет',
    'home.createFirst': 'Создайте свой первый проект',
    'home.loading': 'Загрузка...',
    'home.generated': 'Сгенерирован',
    'home.expired': 'Истёк',
    'home.draft': 'Черновик',
    'home.download': 'Скачать',
    'home.edit': 'Редактировать',
    'home.continue': 'Продолжить',

    'profile.profile': 'Профиль',
    'profile.darkMode': 'Тёмная тема',
    'profile.lightMode': 'Светлая тема',
    'profile.logout': 'Выйти',
    'profile.logoutConfirm': 'Вы уверены, что хотите выйти?',
    'profile.cancel': 'Отмена',

    'delete.title': 'Удалить проект',
    'delete.confirm': 'Вы уверены, что хотите удалить',
    'delete.undone': 'Это действие нельзя отменить.',
    'delete.deleting': 'Удаление...',
    'delete.delete': 'Удалить',

    'profilePage.title': 'Настройки профиля',
    'profilePage.back': 'Назад к панели',
    'profilePage.personal': 'Личные данные',
    'profilePage.changePassword': 'Изменить пароль',
    'profilePage.passwordHint': 'Оставьте пустым, чтобы сохранить текущий пароль',
    'profilePage.newPassword': 'Новый пароль',
    'profilePage.newPasswordPlaceholder': 'Новый пароль (необязательно)',
    'profilePage.save': 'Сохранить',
    'profilePage.saving': 'Сохранение...',
    'profilePage.profileUpdated': 'Профиль успешно обновлён',
    'profilePage.passwordUpdated': 'Профиль и пароль обновлены',
    'profilePage.verified': 'Подтверждён',
    'profilePage.notVerified': 'Не подтверждён',
    'profilePage.verifyHint': 'Подтвердите email для восстановления пароля.',
    'profilePage.sendCode': 'Отправить код подтверждения',
    'profilePage.sending': 'Отправка...',
    'profilePage.codeSent': 'Код подтверждения отправлен на вашу почту',
    'profilePage.enterCode': 'Введите код, отправленный на',
    'profilePage.verify': 'Подтвердить',
    'profilePage.resend': 'Отправить повторно',
    'profilePage.resendIn': 'Повторно через',
    'profilePage.emailVerified': 'Email успешно подтверждён!',
    'profilePage.codeResent': 'Код отправлен повторно',

    // Feedback
    'feedback.title': 'Отзывы и предложения',
    'feedback.subtitle': 'Есть отзывы или предложения? Мы будем рады их услышать.',
    'feedback.backToDashboard': 'Назад к панели',
    'feedback.write': 'Написать отзыв',
    'feedback.aboutLabel': 'Тема',
    'feedback.aboutPlatform': 'Платформа',
    'feedback.aboutGenerated': 'Сгенерированный проект',
    'feedback.aboutOther': 'Другое',
    'feedback.messageLabel': 'Ваш отзыв',
    'feedback.placeholder': 'Поделитесь мыслями, предложениями или сообщите о проблеме...',
    'feedback.submit': 'Отправить',
    'feedback.submitting': 'Отправка...',
    'feedback.submitted': 'Спасибо! Ваш отзыв отправлен.',
    'feedback.error': 'Не удалось отправить отзыв. Попробуйте снова.',
    'feedback.noFeedbacks': 'Отзывов пока нет',
    'feedback.writeFirst': 'Напишите свой первый отзыв',
    'feedback.deleteTitle': 'Удалить отзыв',
    'feedback.deleteConfirm': 'Вы уверены, что хотите удалить этот отзыв?',
    'feedback.prev': 'Назад',
    'feedback.next': 'Далее',
    'feedback.menuItem': 'Отзывы',
    'feedback.bannerTitle': 'Помогите улучшить Backora!',
    'feedback.bannerDesc': 'Это бета-версия. Ваши отзывы и предложения помогают нам создать лучший продукт.',

    // Sidebar
    'sidebar.projects': 'Проекты',
    'sidebar.myProjects': 'Мои проекты',
    'sidebar.chooseTemplate': 'Выбрать шаблон',
    'sidebar.newProject': 'Новый проект',
    'sidebar.feedback': 'Отзывы',
    'sidebar.profile': 'Профиль',
    'sidebar.settings': 'Настройки',
    'sidebar.admin': 'Админ страница',

    'landing.browseTemplates': 'Шаблоны',

    'templates.heroTitle': 'Выберите отправную точку',
    'templates.heroSub': 'Возьмите готовую схему, настройте и разверните бэкенд за секунды.',
    'templates.use': 'Использовать шаблон',
    'templates.entities': 'таблиц',
    'templates.blog.name': 'Blog API',
    'templates.blog.desc': 'Классический бэкенд блога: пользователи, посты, комментарии и теги. Готов для контент-платформ или портфолио.',
    'templates.tasks.name': 'Task Tracker',
    'templates.tasks.desc': 'Project · Board · Task · Comment — API управления задачами в стиле Trello с пользователями и назначениями.',
    'templates.ecommerce.name': 'E-commerce Starter',
    'templates.ecommerce.desc': 'User · Category · Product · Order · OrderItem — базовая схема каталога магазина и оформления заказа.',
    'templates.customBadge': 'Свой',

    'admin.templates': 'Шаблоны',
    'admin.template.new': 'Новый шаблон',
    'admin.template.edit': 'Редактировать',
    'admin.template.source': 'Источник',
    'admin.template.entities': 'Таблицы',
    'admin.template.builtin': 'Встроенный',
    'admin.template.custom': 'Свой',
    'admin.template.descShort': 'Описание',
    'admin.template.empty': 'Шаблонов пока нет',
    'admin.template.deleteTitle': 'Удалить шаблон',
    'admin.template.deleteConfirm': 'Вы уверены что хотите удалить',
    'admin.template.namePlaceholder': 'Название шаблона',
    'admin.template.descPlaceholder': 'Краткое описание (видят пользователи)',
    'admin.template.color': 'Цвет',
    'admin.template.icon': 'Иконка',
    'admin.template.save': 'Сохранить шаблон',
    'admin.template.saving': 'Сохранение...',
    'admin.template.nameRequired': 'Введите название шаблона',
    'admin.template.backToAdmin': 'Назад в админ',

    'guest.previewMode': 'Режим просмотра — войдите для редактирования и генерации',
    'guest.signInToEdit': 'Войти для редактирования',
    'guest.backToTemplates': 'Назад к шаблонам',

    // Admin
    'admin.panel': 'Админ панель',
    'admin.loginTitle': 'Админ верификация',
    'admin.loginIdleHint': 'Требуется админ верификация. Нажмите кнопку, чтобы отправить 6-значный код на админ email.',
    'admin.loginHint': 'На админ email отправлен 6-значный код. Введите его ниже для продолжения.',
    'admin.sendCode': 'Отправить код подтверждения',
    'admin.code': 'Код подтверждения',
    'admin.codeSent': 'Код отправлен на админ email',
    'admin.codeLength': 'Введите 6-значный код',
    'admin.sendFailed': 'Ошибка отправки кода',
    'admin.verifyFailed': 'Неверный или истёкший код',
    'admin.verify': 'Подтвердить',
    'admin.users': 'Пользователи',
    'admin.projects': 'Проекты',
    'admin.noUsers': 'Пользователи не найдены',
    'admin.noProjects': 'Проекты не найдены',
    'admin.locked': 'Админ панель закрыта. Подтвердите кодом с админ email.',
    'admin.unlock': 'Открыть',
    'admin.signOut': 'Выйти из админки',
    'admin.col.id': 'ID',
    'admin.col.name': 'Имя',
    'admin.col.email': 'Email',
    'admin.col.provider': 'Провайдер',
    'admin.col.verified': 'Подтверждён',
    'admin.col.createdAt': 'Создан',
    'admin.col.projectName': 'Проект',
    'admin.col.owner': 'Владелец',
    'admin.col.ownerEmail': 'Email владельца',
    'admin.col.language': 'Язык',
    'admin.col.status': 'Статус',
    'admin.col.balance': 'Баланс',
    'admin.col.expiredAt': 'Истекает',
    'admin.status.generated': 'Сгенерирован',
    'admin.status.expired': 'Истёк',
    'admin.status.draft': 'Черновик',
    'admin.view': 'Просмотр',
    'admin.viewingBadge': 'ТОЛЬКО ПРОСМОТР',
    'admin.backToPanel': 'Назад в админ панель',
    'admin.rowsPerPage': 'Строк на странице',
    'admin.totalRows': 'всего',

    // Relation hover preview mode
    'hoverMode.label': 'Hover:',
    'hoverMode.code': 'Код',
    'hoverMode.json': 'JSON',
    'hoverMode.tooltip': 'Выберите вид превью для hover на связи',

    'error.connection': 'Не удалось подключиться к серверу',
  },
};

export function useT() {
  const lang = useStore((s) => s.uiLang);
  return useCallback(
    (key) => translations[lang]?.[key] || translations.en[key] || key,
    [lang]
  );
}

export default translations;
